import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Paper,
  IconButton,
  Divider,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Http as HttpIcon,
} from '@mui/icons-material';
import { DEBUG_COLORS } from '../config';
import { store, ApiRequest } from '../utils/apiRequestStore';
import { useNotification } from '../context/NotificationContext';

export const ApiRequests: React.FC = () => {
  const theme = useTheme();
  const { updateCount } = useNotification();
  const [requests, setRequests] = useState<ApiRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<ApiRequest | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<string>('all'); // all, fetch, xhr
  const [searchQuery, setSearchQuery] = useState('');
  const [detailTab, setDetailTab] = useState<Record<string, number>>({}); // Tab index for each expanded request
  const [preserveLog, setPreserveLog] = useState(false);

  // Subscribe to global store
  useEffect(() => {
    const unsubscribe = store.subscribe((newRequests) => {
      setRequests(newRequests);
      // Update notification count
      updateCount('api-requests', newRequests.length);
    });
    return unsubscribe;
  }, [updateCount]);

  const clearRequests = () => {
    store.clearRequests();
    setSelectedRequest(null);
    setExpandedRows(new Set());
  };

  const filteredRequests = useMemo(() => {
    return requests.filter((req) => {
      // Type filter
      if (typeFilter !== 'all' && req.type !== typeFilter) {
        return false;
      }

      // Search filter
      if (searchQuery && !req.url.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      return true;
    });
  }, [requests, typeFilter, searchQuery]);

  const getStatusColor = (status?: number): string => {
    if (!status) return theme.palette.text.secondary; // Gray for pending
    if (status >= 200 && status < 300) return theme.palette.success.main; // Green for success
    if (status >= 300 && status < 400) return theme.palette.warning.main; // Orange for redirect
    if (status >= 400) return theme.palette.error.main; // Red for error
    return theme.palette.text.secondary;
  };

  const getStatusText = (status?: number): string => {
    if (!status) return 'Pending';
    if (status >= 200 && status < 300) return status.toString();
    if (status >= 300 && status < 400) return status.toString();
    if (status >= 400) return status.toString();
    return 'Unknown';
  };

  const getSize = (request: ApiRequest): string => {
    if (request.responseData) {
      const size = JSON.stringify(request.responseData).length;
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} kB`;
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }
    return '-';
  };

  const getFileName = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const parts = pathname.split('/');
      return parts[parts.length - 1] || pathname || url;
    } catch {
      const parts = url.split('/');
      return parts[parts.length - 1] || url;
    }
  };

  const toggleRowExpand = (requestId: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
        const newTabs = { ...detailTab };
        delete newTabs[requestId];
        setDetailTab(newTabs);
      } else {
        newSet.add(requestId);
        setDetailTab({ ...detailTab, [requestId]: 0 });
      }
      return newSet;
    });
  };

  const handleDetailTabChange = (requestId: string, newValue: number) => {
    setDetailTab({ ...detailTab, [requestId]: newValue });
  };

  const copyRequestAsCurl = (request: ApiRequest) => {
    try {
      let curl = `curl -X ${request.method}`;
      
      if (request.requestHeaders) {
        Object.entries(request.requestHeaders).forEach(([key, value]) => {
          curl += ` \\\n  -H "${key}: ${value}"`;
        });
      }

      if (request.requestPayload) {
        curl += ` \\\n  -d '${JSON.stringify(request.requestPayload)}'`;
      }

      curl += ` \\\n  "${request.url}"`;

      navigator.clipboard.writeText(curl);
    } catch (error) {
      console.error('Failed to copy as cURL:', error);
    }
  };

  const copyRequestData = async (data: any) => {
    try {
      const dataStr = typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data);
      await navigator.clipboard.writeText(dataStr);
    } catch (error) {
      console.error('Failed to copy data:', error);
    }
  };


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: theme.palette.background.default }}>
      {/* Toolbar - Chrome DevTools style */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          bgcolor: theme.palette.background.default,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexWrap: 'wrap',
        }}
      >
        {/* Filter Buttons */}
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Chip
            label="All"
            size="small"
            onClick={() => setTypeFilter('all')}
            sx={{
              bgcolor: typeFilter === 'all' ? theme.palette.primary.main : 'transparent',
              color: typeFilter === 'all' ? theme.palette.primary.contrastText : theme.palette.text.primary,
              border: `1px solid ${theme.palette.divider}`,
              height: 24,
              fontSize: '0.75rem',
              cursor: 'pointer',
              '&:hover': { bgcolor: typeFilter === 'all' ? theme.palette.primary.dark : theme.palette.action.hover },
            }}
          />
          <Chip
            label="Fetch/XHR"
            size="small"
            onClick={() => setTypeFilter('xhr')}
            sx={{
              bgcolor: typeFilter === 'xhr' ? theme.palette.primary.main : 'transparent',
              color: typeFilter === 'xhr' ? theme.palette.primary.contrastText : theme.palette.text.primary,
              border: `1px solid ${theme.palette.divider}`,
              height: 24,
              fontSize: '0.75rem',
              cursor: 'pointer',
              '&:hover': { bgcolor: typeFilter === 'xhr' ? theme.palette.primary.dark : theme.palette.action.hover },
            }}
          />
          <Chip
            label="Fetch"
            size="small"
            onClick={() => setTypeFilter('fetch')}
            sx={{
              bgcolor: typeFilter === 'fetch' ? theme.palette.primary.main : 'transparent',
              color: typeFilter === 'fetch' ? theme.palette.primary.contrastText : theme.palette.text.primary,
              border: `1px solid ${theme.palette.divider}`,
              height: 24,
              fontSize: '0.75rem',
              cursor: 'pointer',
              '&:hover': { bgcolor: typeFilter === 'fetch' ? theme.palette.primary.dark : theme.palette.action.hover },
            }}
          />
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {/* Search */}
        <TextField
          size="small"
          placeholder="Filter by URL..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />,
          }}
          sx={{
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              height: 28,
              fontSize: '0.75rem',
              bgcolor: theme.palette.background.paper,
            },
          }}
        />

        <Box sx={{ flex: 1 }} />

        {/* Options */}
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={preserveLog}
              onChange={(e) => setPreserveLog(e.target.checked)}
            />
          }
          label="Preserve log"
          sx={{ fontSize: '0.75rem', '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
        />

        {/* Clear Button */}
        <IconButton
          size="small"
          onClick={clearRequests}
          disabled={requests.length === 0}
          sx={{
            p: 0.5,
            '&:hover': { bgcolor: theme.palette.action.hover },
          }}
          title="Clear all requests"
        >
          <DeleteIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Requests Table - Chrome DevTools style */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', bgcolor: theme.palette.background.paper }}>
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            {/* Table Header */}
            <Box
              component="thead"
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: theme.palette.background.default,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box component="tr" sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Box
                  component="th"
                  sx={{
                    px: 1,
                    py: 0.5,
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: '11px',
                    color: theme.palette.text.secondary,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    width: '40px',
                    minWidth: '40px',
                  }}
                >
                  {/* Status indicator column header */}
                </Box>
                <Box
                  component="th"
                  sx={{
                    px: 1,
                    py: 0.5,
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: '11px',
                    color: theme.palette.text.secondary,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    width: '80px',
                    minWidth: '80px',
                  }}
                >
                  Method
                </Box>
                <Box
                  component="th"
                  sx={{
                    px: 1,
                    py: 0.5,
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: '11px',
                    color: theme.palette.text.secondary,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    minWidth: '200px',
                  }}
                >
                  Name
                </Box>
                <Box
                  component="th"
                  sx={{
                    px: 1,
                    py: 0.5,
                    textAlign: 'right',
                    fontWeight: 600,
                    fontSize: '11px',
                    color: theme.palette.text.secondary,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    width: '60px',
                    minWidth: '60px',
                  }}
                >
                  Status
                </Box>
                <Box
                  component="th"
                  sx={{
                    px: 1,
                    py: 0.5,
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: '11px',
                    color: theme.palette.text.secondary,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    width: '80px',
                    minWidth: '80px',
                  }}
                >
                  Type
                </Box>
                <Box
                  component="th"
                  sx={{
                    px: 1,
                    py: 0.5,
                    textAlign: 'right',
                    fontWeight: 600,
                    fontSize: '11px',
                    color: theme.palette.text.secondary,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    width: '70px',
                    minWidth: '70px',
                  }}
                >
                  Size
                </Box>
                <Box
                  component="th"
                  sx={{
                    px: 1,
                    py: 0.5,
                    textAlign: 'right',
                    fontWeight: 600,
                    fontSize: '11px',
                    color: theme.palette.text.secondary,
                    width: '70px',
                    minWidth: '70px',
                  }}
                >
                  Time
                </Box>
              </Box>
            </Box>

            {/* Table Body */}
            <Box component="tbody">
              {filteredRequests.length > 0 ? (
                filteredRequests.map((request) => {
                  const isExpanded = expandedRows.has(request.id);
                  const isSelected = selectedRequest?.id === request.id;
                  const currentTab = detailTab[request.id] || 0;
                  const fileName = getFileName(request.url);
                  const statusColor = getStatusColor(request.status);

                  return (
                    <React.Fragment key={request.id}>
                      {/* Main Row */}
                      <Box
                        component="tr"
                        onClick={() => {
                          setSelectedRequest(request);
                          toggleRowExpand(request.id);
                        }}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: isSelected 
                            ? theme.palette.action.selected 
                            : isExpanded 
                            ? theme.palette.background.default 
                            : theme.palette.background.paper,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          '&:hover': { bgcolor: isSelected ? theme.palette.action.selected : theme.palette.action.hover },
                        }}
                      >
                        {/* Status Indicator */}
                        <Box
                          component="td"
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRight: `1px solid ${theme.palette.divider}`,
                            width: '40px',
                          }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: statusColor,
                              display: 'inline-block',
                            }}
                          />
                        </Box>

                        {/* Method */}
                        <Box
                          component="td"
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRight: `1px solid ${theme.palette.divider}`,
                            width: '80px',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: statusColor,
                          }}
                        >
                          {request.method}
                        </Box>

                        {/* Name */}
                        <Box
                          component="td"
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRight: `1px solid ${theme.palette.divider}`,
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            color: theme.palette.primary.main,
                            maxWidth: '400px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={request.url}
                        >
                          {fileName}
                        </Box>

                        {/* Status */}
                        <Box
                          component="td"
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRight: `1px solid ${theme.palette.divider}`,
                            width: '60px',
                            textAlign: 'right',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            color: statusColor,
                            fontWeight: request.status && request.status >= 400 ? 600 : 400,
                          }}
                        >
                          {getStatusText(request.status)}
                        </Box>

                        {/* Type */}
                        <Box
                          component="td"
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRight: `1px solid ${theme.palette.divider}`,
                            width: '80px',
                            fontSize: '11px',
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {request.type.toUpperCase()}
                        </Box>

                        {/* Size */}
                        <Box
                          component="td"
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRight: `1px solid ${theme.palette.divider}`,
                            width: '70px',
                            textAlign: 'right',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {getSize(request)}
                        </Box>

                        {/* Time */}
                        <Box
                          component="td"
                          sx={{
                            px: 1,
                            py: 0.5,
                            width: '70px',
                            textAlign: 'right',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {request.responseTime !== undefined ? `${request.responseTime} ms` : '-'}
                        </Box>
                      </Box>

                      {/* Expanded Details Row */}
                      {isExpanded && (
                        <Box component="tr">
                          <Box
                            component="td"
                            colSpan={7}
                            sx={{
                              p: 0,
                              bgcolor: 'white',
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Box sx={{ borderTop: '1px solid #e0e0e0' }}>
                              {/* Tabs */}
                              <Tabs
                                value={currentTab}
                                onChange={(e, newValue) => handleDetailTabChange(request.id, newValue)}
                                sx={{
                                  minHeight: 32,
                                  borderBottom: `1px solid ${theme.palette.divider}`,
                                  '& .MuiTab-root': {
                                    minHeight: 32,
                                    fontSize: '11px',
                                    textTransform: 'none',
                                    px: 2,
                                  },
                                }}
                              >
                                <Tab label="Headers" />
                                <Tab label="Preview" />
                                <Tab label="Response" />
                                <Tab label="Timing" />
                              </Tabs>

                              {/* Tab Content */}
                              <Box sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                                {currentTab === 0 && (
                                  <Box>
                                    {/* Request Headers */}
                                    {request.requestHeaders && Object.keys(request.requestHeaders).length > 0 && (
                                      <Box sx={{ mb: 2 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 600,
                                            fontSize: '11px',
                                            color: theme.palette.text.secondary,
                                            display: 'block',
                                            mb: 1,
                                          }}
                                        >
                                          Request Headers
                                        </Typography>
                                        <Box
                                          sx={{
                                            bgcolor: theme.palette.background.default,
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                            p: 1,
                                            fontFamily: 'monospace',
                                            fontSize: '11px',
                                          }}
                                        >
                                          {Object.entries(request.requestHeaders).map(([key, value]) => (
                                            <Box
                                              key={key}
                                              sx={{
                                                display: 'flex',
                                                mb: 0.5,
                                                '&:last-child': { mb: 0 },
                                              }}
                                            >
                                              <Box
                                                component="span"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: theme.palette.primary.main,
                                                  minWidth: 150,
                                                  mr: 1,
                                                }}
                                              >
                                                {key}:
                                              </Box>
                                              <Box component="span" sx={{ color: theme.palette.text.primary }}>
                                                {value}
                                              </Box>
                                            </Box>
                                          ))}
                                        </Box>
                                      </Box>
                                    )}

                                    {/* Request Payload */}
                                    {request.requestPayload && (
                                      <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: '11px',
                                              color: theme.palette.text.secondary,
                                            }}
                                          >
                                            Request Payload
                                          </Typography>
                                          <IconButton
                                            size="small"
                                            onClick={() => copyRequestData(request.requestPayload)}
                                            sx={{ p: 0.5 }}
                                          >
                                            <CopyIcon sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        </Box>
                                        <Box
                                          sx={{
                                            bgcolor: theme.palette.background.default,
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                            p: 1,
                                            fontFamily: 'monospace',
                                            fontSize: '11px',
                                            maxHeight: 200,
                                            overflow: 'auto',
                                          }}
                                        >
                                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                            {JSON.stringify(request.requestPayload, null, 2)}
                                          </pre>
                                        </Box>
                                      </Box>
                                    )}

                                    {/* Response Headers */}
                                    {request.responseHeaders && Object.keys(request.responseHeaders).length > 0 && (
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 600,
                                            fontSize: '11px',
                                            color: theme.palette.text.secondary,
                                            display: 'block',
                                            mb: 1,
                                          }}
                                        >
                                          Response Headers
                                        </Typography>
                                        <Box
                                          sx={{
                                            bgcolor: theme.palette.background.default,
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                            p: 1,
                                            fontFamily: 'monospace',
                                            fontSize: '11px',
                                          }}
                                        >
                                          {Object.entries(request.responseHeaders).map(([key, value]) => (
                                            <Box
                                              key={key}
                                              sx={{
                                                display: 'flex',
                                                mb: 0.5,
                                                '&:last-child': { mb: 0 },
                                              }}
                                            >
                                              <Box
                                                component="span"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: theme.palette.primary.main,
                                                  minWidth: 150,
                                                  mr: 1,
                                                }}
                                              >
                                                {key}:
                                              </Box>
                                              <Box component="span" sx={{ color: theme.palette.text.primary }}>
                                                {value}
                                              </Box>
                                            </Box>
                                          ))}
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                )}

                                {currentTab === 1 && (
                                  <Box>
                                    {request.responseData ? (
                                      <Box
                                        sx={{
                                          bgcolor: '#fafafa',
                                          border: '1px solid #e0e0e0',
                                          borderRadius: 1,
                                          p: 1,
                                          fontFamily: 'monospace',
                                          fontSize: '11px',
                                          maxHeight: 300,
                                          overflow: 'auto',
                                        }}
                                      >
                                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                          {typeof request.responseData === 'string'
                                            ? request.responseData
                                            : JSON.stringify(request.responseData, null, 2)}
                                        </pre>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" sx={{ fontSize: '11px', color: theme.palette.text.disabled }}>
                                        No preview available
                                      </Typography>
                                    )}
                                  </Box>
                                )}

                                {currentTab === 2 && (
                                  <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: '11px',
                                          color: theme.palette.text.secondary,
                                        }}
                                      >
                                        Response
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        onClick={() => copyRequestData(request.responseData)}
                                        sx={{ p: 0.5 }}
                                      >
                                        <CopyIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Box>
                                    {request.responseData ? (
                                      <Box
                                        sx={{
                                          bgcolor: '#fafafa',
                                          border: '1px solid #e0e0e0',
                                          borderRadius: 1,
                                          p: 1,
                                          fontFamily: 'monospace',
                                          fontSize: '11px',
                                          maxHeight: 300,
                                          overflow: 'auto',
                                        }}
                                      >
                                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                          {typeof request.responseData === 'string'
                                            ? request.responseData
                                            : JSON.stringify(request.responseData, null, 2)}
                                        </pre>
                                      </Box>
                                    ) : request.error ? (
                                      <Box
                                        sx={{
                                          bgcolor: theme.palette.error.light,
                                          border: `1px solid ${theme.palette.error.main}`,
                                          borderRadius: 1,
                                          p: 1,
                                          fontFamily: 'monospace',
                                          fontSize: '11px',
                                          color: theme.palette.error.main,
                                        }}
                                      >
                                        {request.error}
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" sx={{ fontSize: '11px', color: theme.palette.text.disabled }}>
                                        No response data
                                      </Typography>
                                    )}
                                  </Box>
                                )}

                                {currentTab === 3 && (
                                  <Box>
                                    <Box sx={{ mb: 2 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: '11px',
                                          color: theme.palette.text.secondary,
                                          display: 'block',
                                          mb: 1,
                                        }}
                                      >
                                        Timing
                                      </Typography>
                                      <Box
                                        sx={{
                                          bgcolor: '#fafafa',
                                          border: '1px solid #e0e0e0',
                                          borderRadius: 1,
                                          p: 1,
                                          fontFamily: 'monospace',
                                          fontSize: '11px',
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                          <Box component="span" sx={{ color: '#666' }}>
                                            Total Time:
                                          </Box>
                                          <Box component="span" sx={{ fontWeight: 600 }}>
                                            {request.responseTime !== undefined ? `${request.responseTime} ms` : 'Pending'}
                                          </Box>
                                        </Box>
                                        {request.timestamp && (
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Box component="span" sx={{ color: '#666' }}>
                                              Started:
                                            </Box>
                                            <Box component="span">
                                              {new Date(request.timestamp).toLocaleTimeString()}
                                            </Box>
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>

                                    {/* Copy as cURL */}
                                    <Box>
                                      <Button
                                        size="small"
                                        startIcon={<CopyIcon />}
                                        onClick={() => copyRequestAsCurl(request)}
                                        sx={{
                                          fontSize: '11px',
                                          textTransform: 'none',
                                          height: 28,
                                        }}
                                      >
                                        Copy as cURL
                                      </Button>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <Box component="tr">
                  <Box
                    component="td"
                    colSpan={7}
                    sx={{
                      py: 4,
                      textAlign: 'center',
                      color: '#999',
                      fontSize: '12px',
                    }}
                  >
                    No requests captured yet. Make some API calls to see them here.
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Footer - Chrome DevTools style */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 1,
            py: 0.5,
            bgcolor: theme.palette.background.default,
            borderTop: `1px solid ${theme.palette.divider}`,
            fontSize: '11px',
            color: theme.palette.text.secondary,
          }}
        >
          <Box component="span">
            {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''}
            {filteredRequests.length !== requests.length && ` (${requests.length} total)`}
          </Box>
          <Box component="span" sx={{ fontFamily: 'monospace' }}>
            {new Date().toLocaleTimeString()}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

